<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from "vue";

import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";

defineOptions({ inheritAttrs: false });

defineProps<{ active: "left" | "right" }>();

const toggleLeftRef = useTemplateRef("toggleLeft");
const toggleRightRef = useTemplateRef("toggleRight");

const toggleLeftWidth = ref<{ right?: string } | null>(null);
const toggleRightWidth = ref<{ left?: string } | null>(null);
const isMeasured = ref(false);

/**
 * Animate the 'active' indicator by updating left/right based on which prop is active.
 * Measures the size of the left/right 'buttons' to position the indicator correctly
 * (the buttons can be different sizes)
 */
const updateWidths = () => {
  if (toggleLeftRef.value && toggleRightRef.value) {
    const widthLeft = toggleLeftRef.value.getBoundingClientRect().width;
    const widthRight = toggleRightRef.value.getBoundingClientRect().width;

    toggleLeftWidth.value = { right: `calc(100% - ${widthLeft + 2}px)` };
    toggleRightWidth.value = { left: `calc(100% - ${widthRight + 2}px)` };

    isMeasured.value = true;
  }
};

onMounted(updateWidths);
</script>

<template>
  <BaseTooltip>
    <template v-if="$slots.tooltip" #content>
      <slot name="tooltip" />
    </template>
    <button class="toggle-button" :class="{ 'right-active': active === 'right' }" v-bind="$attrs">
      <div
        v-if="isMeasured"
        class="active-indicator"
        :style="active === 'left' ? toggleLeftWidth : toggleRightWidth"
      />
      <div ref="toggleLeft" class="side left" :class="{ active: active === 'left' }">
        <slot name="left" />
      </div>
      <div ref="toggleRight" class="side right" :class="{ active: active === 'right' }">
        <slot name="right" />
      </div>
    </button>
  </BaseTooltip>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

$button-padding: 2px;

.toggle-button {
  @include typography.medium("semi-bold");

  cursor: pointer;
  outline: revert;
  outline-offset: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $button-padding;
  border: 1px solid colors.$border-subtle;
  border-radius: 6px;
  padding: $button-padding;
  position: relative;
  height: 36px;

  .active-indicator {
    content: "";
    position: absolute;
    inset: $button-padding;
    right: calc(50% + $button-padding / 2); // Fallback - assume equal sized buttons
    background-color: colors.$main;
    border-radius: 4px;
    transition: all 0.3s ease;
  }

  &.right-active .active-indicator {
    left: calc(50% + $button-padding / 2); // Fallback - assume equal sized buttons
    right: $button-padding;
  }

  .side {
    padding: 6px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: colors.$icon-subtle;
    position: relative;
    min-width: 32px;
    min-height: 32px;

    &.active {
      color: colors.$icon-inverse;
    }
  }
}
</style>
